/**
 * Task reschedule editor (SCH.4).
 *
 * Lets a supervisor set or change a task's date/time after approval — refine
 * the seeded window (pickup Monday, install Tuesday) or move it. A calendar
 * dialog (existing schedule in view) picks the day/range; time-of-day inputs set
 * the hours. Saving PATCHes the task; the API notifies the PM + crew via
 * project_scheduled. Reuses the day↔ISO convention of the intake pickers.
 */
import { useState } from 'react';
import { CalendarClock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useUpdateTask } from '@/api/tasks';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Input, Label } from '@/components/ui/Input';
import { CalendarDatePicker } from '@/components/ui/CalendarDatePicker';
import { dayTimeToIso, isoToDay, isoToTime } from '@/lib/calendarGrid';

export interface TaskScheduleButtonProps {
  task: {
    id: string;
    title: string;
    scheduled_start: string | null;
    scheduled_end: string | null;
  };
  /** Parent-project context for the calendar: the committed/requested days, the
   *  requested working-hours label, and the hard deadline — so the supervisor
   *  reschedules against the project's window, not blind. */
  requestedDays?: string[];
  deadline?: string | null;
  hoursLabel?: string | null;
}

export function TaskScheduleButton({
  task,
  requestedDays,
  deadline,
  hoursLabel,
}: TaskScheduleButtonProps) {
  const update = useUpdateTask(task.id);
  const [open, setOpen] = useState(false);
  const [startDay, setStartDay] = useState<string | null>(null);
  const [endDay, setEndDay] = useState<string | null>(null);
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('12:00');
  const [includeWeekends, setIncludeWeekends] = useState(false);

  function openDialog() {
    setStartDay(isoToDay(task.scheduled_start));
    setEndDay(isoToDay(task.scheduled_end));
    setStartTime(isoToTime(task.scheduled_start) ?? '08:00');
    setEndTime(isoToTime(task.scheduled_end) ?? '12:00');
    setIncludeWeekends(false);
    setOpen(true);
  }

  const startIso = startDay ? dayTimeToIso(startDay, startTime) : null;
  const endIso = startDay ? dayTimeToIso(endDay ?? startDay, endTime) : null;
  const endBeforeStart = !!startIso && !!endIso && endIso <= startIso;

  function save() {
    if (!startIso || !endIso || endBeforeStart) return;
    update.mutate(
      { scheduled_start: startIso, scheduled_end: endIso },
      {
        onSuccess: () => {
          toast.success('Task rescheduled');
          setOpen(false);
        },
        onError: (err) => toast.error(`Reschedule failed: ${err.message}`),
      },
    );
  }

  function clearSchedule() {
    update.mutate(
      { scheduled_start: null, scheduled_end: null },
      {
        onSuccess: () => {
          toast.success('Task unscheduled');
          setOpen(false);
        },
        onError: (err) => toast.error(`Failed: ${err.message}`),
      },
    );
  }

  const isScheduled = !!task.scheduled_start;

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          openDialog();
        }}
        className="inline-flex items-center gap-1 text-[11px] text-ink-faint hover:text-accent shrink-0"
      >
        <CalendarClock className="w-3.5 h-3.5" />
        {isScheduled ? 'Reschedule' : 'Schedule'}
      </button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        busy={update.isPending}
        title={
          <>
            <CalendarClock className="w-4 h-4" />
            {isScheduled ? 'Reschedule task' : 'Schedule task'}
          </>
        }
      >
              <div className="p-4 space-y-3">
                <div className="font-medium text-sm truncate">{task.title}</div>
                <CalendarDatePicker
                  start={startDay}
                  end={endDay}
                  onChange={(r) => {
                    setStartDay(r.start);
                    setEndDay(r.end);
                  }}
                  requestedDays={requestedDays}
                  requestedLabel="Committed"
                  deadline={deadline}
                  hoursLabel={hoursLabel}
                  includeWeekends={includeWeekends}
                  onIncludeWeekendsChange={setIncludeWeekends}
                />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor={`start-${task.id}`}>Start time</Label>
                    <Input
                      id={`start-${task.id}`}
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`end-${task.id}`}>End time</Label>
                    <Input
                      id={`end-${task.id}`}
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                    />
                  </div>
                </div>
                {endBeforeStart && (
                  <p className="text-[11px] text-status-blocked-fg">
                    End must be after start.
                  </p>
                )}
                <div className="flex items-center justify-between gap-2 pt-1">
                  <button
                    type="button"
                    className="text-[11px] text-ink-faint hover:text-ink underline disabled:opacity-50"
                    disabled={update.isPending || !isScheduled}
                    onClick={clearSchedule}
                  >
                    Clear schedule
                  </button>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setOpen(false)}
                      disabled={update.isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={save}
                      disabled={update.isPending || !startDay || endBeforeStart}
                    >
                      {update.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                      Save
                    </Button>
                  </div>
                </div>
              </div>
      </Dialog>
    </>
  );
}
